import { sendGAEvent } from "../../AnalyticsService";
import { VESSEL_VIEW, VESSEL_VIEW_OPENED } from "../../constants/analytics";
import { getGradeImage, getShipImage, getShipParticulars } from "../../services/VesselViewService";
import { FleetDataItem } from "../../types/FleetDataItem";
import {  selectFleetDataItems } from "../selectors/fleetSelectors";
import { setSelectedVessel, setVesselGradeImage, setVesselGradeImageError, setVesselGradeImageLoading, setVesselImage, setVesselImageError, setVesselImageLoading, setVesselParticulars, setVesselParticularsError, setVesselParticularsLoading, setVesselViewOpen } from "../slices/vesselViewSlice";
import { AppThunk } from "../store";

export const selectVesselByImo = (vesselImo: string): AppThunk => async (dispatch, getState) => {
    const fleetDataItems = selectFleetDataItems(getState());

    const selectedVessel = fleetDataItems.find(
        (fleetDataItem) => fleetDataItem.vessel.imo.toUpperCase() === vesselImo.toUpperCase()
    );

    const getLatestInspection = (selectedVessel: FleetDataItem) => {
        if (!selectedVessel.scoreHistory || selectedVessel.scoreHistory.length === 0) {
            return null;
        }

        const latestInspection = selectedVessel.scoreHistory
            .filter(item => item.issuedAt && item.dateFrom)
            .sort((a, b) => {
                const dateA = a.issuedAt ?? a.dateFrom!;
                const dateB = b.issuedAt ?? b.dateFrom!;
                return new Date(dateB).getTime() - new Date(dateA).getTime();
            })[0];
        return latestInspection;
    }

    if (selectedVessel) {
        dispatch(setSelectedVessel(selectedVessel));

        sendGAEvent(VESSEL_VIEW, VESSEL_VIEW_OPENED);
        dispatch(setVesselViewOpen(true));
        dispatch(getVesselParticulars(selectedVessel.vessel.imo));
        dispatch(getVesselImage(selectedVessel.vessel.imo));

        const latestInspection = getLatestInspection(selectedVessel);
        dispatch(getVesselGradeImage(selectedVessel.vessel.imo, latestInspection?.id ?? ""));
    } else {
        console.warn("Unable to find vessel with IMO: ", vesselImo);
    }
};

const getVesselParticulars = (imo: string): AppThunk => async (dispatch) => {
    dispatch(setVesselParticularsLoading(true));
    dispatch(setVesselParticularsError(false));

    try {
        const response = await getShipParticulars(imo);

        if (response) {
            dispatch(setVesselParticulars(response));
        } else {
            throw Error('no data');
        }
    } catch (e) {
        console.error(e);
        dispatch(setVesselParticularsError(true));
    } finally {
        dispatch(setVesselParticularsLoading(false));
    }
}

const getVesselImage = (imo: string): AppThunk => async (dispatch) => {
    dispatch(setVesselImageLoading(true));
    dispatch(setVesselImageError(false));

    try {
        const response = await getShipImage(imo);

        if (response) {
            dispatch(setVesselImage(response));
        } else {
            throw Error('no data');
        }
    } catch (e) {
        console.error(e);
        dispatch(setVesselImageError(true));
    } finally {
        dispatch(setVesselImageLoading(false));
    }
}

const getVesselGradeImage = (imo: string, inspectionId: string): AppThunk => async (dispatch) => {
    if (!inspectionId) {
        dispatch(setVesselGradeImage(""));
        return;
    }

    dispatch(setVesselGradeImageLoading(true));
    dispatch(setVesselGradeImageError(false));
    dispatch(setVesselGradeImage(""))

    try {
        const response = await getGradeImage(imo, inspectionId);

        if (response) {
            dispatch(setVesselGradeImage(response));
        } else {
            throw Error('no data');
        }
    } catch (e) {
        console.error(e);
        dispatch(setVesselGradeImageError(true));
    } finally {
        dispatch(setVesselGradeImageLoading(false));
    }
}
